/**
 * Settings Panel - UI for game settings including audio controls
 */

import { audioManager, type AudioSettings } from '@/core/audio/AudioManager';
import { COMMUNITY_LINKS } from '@/ui/CommunityLinks';

export class SettingsPanel {
  private container: HTMLElement;
  private visible: boolean = false;

  constructor() {
    this.container = document.createElement('div');
    this.container.id = 'settings-panel';
    this.container.innerHTML = `
      <style>
        #settings-panel {
          position: fixed;
          left: 50%;
          top: 50%;
          transform: translate(-50%, -50%);
          z-index: 310;
          font: 13px/1.4 'Noto Serif SC', 'SimSun', serif;
          color: #f5e6d3;
          user-select: none;
          min-width: 380px;
          display: none;
        }
        #settings-panel.visible {
          display: block;
        }
        .settings-panel-wrapper {
          background:
            linear-gradient(145deg, rgba(74,55,40,0.85) 0%, rgba(45,31,21,0.9) 100%),
            url('/images/ui/elements/wood.webp');
          background-size: cover;
          background-position: center;
          border: 3px solid #8b6914;
          border-radius: 8px;
          box-shadow:
            inset 0 2px 4px rgba(255,220,150,0.15),
            inset 0 -3px 6px rgba(0,0,0,0.4),
            0 10px 40px rgba(0,0,0,0.6);
          overflow: hidden;
          position: relative;
        }
        /* Dragon corner ornaments */
        .settings-panel-corner {
          position: absolute;
          width: 40px;
          height: 40px;
          background-image: url('/images/ui/decorations/corner-ornaments.png');
          background-size: 200% 200%;
          z-index: 2;
          filter: drop-shadow(0 2px 3px rgba(0,0,0,0.4));
          pointer-events: none;
        }
        .settings-panel-corner.tl { top: -6px; left: -6px; background-position: 0% 0%; }
        .settings-panel-corner.tr { top: -6px; right: -6px; background-position: 100% 0%; }
        .settings-panel-corner.bl { bottom: -6px; left: -6px; background-position: 0% 100%; }
        .settings-panel-corner.br { bottom: -6px; right: -6px; background-position: 100% 100%; }
        .settings-panel-title-bar {
          background: linear-gradient(180deg, #6b4423 0%, #4a2f17 100%);
          border-bottom: 2px solid #8b6914;
          padding: 12px 16px;
          display: flex;
          justify-content: space-between;
          align-items: center;
        }
        .settings-panel-title {
          font: 700 16px/1 'Noto Serif SC', 'SimSun', serif;
          color: #ffd35a;
          text-shadow: 0 2px 4px rgba(0,0,0,0.5);
        }
        #settings-panel .close-btn {
          background: none;
          border: none;
          color: #c4a77d;
          cursor: pointer;
          font-size: 20px;
          padding: 0 4px;
          transition: color 0.2s;
        }
        #settings-panel .close-btn:hover {
          color: #ffd35a;
        }
        .settings-panel-content {
          padding: 16px;
        }
        #settings-panel .settings-section {
          margin-bottom: 16px;
        }
        #settings-panel .section-title {
          font-weight: 600;
          font-size: 14px;
          margin-bottom: 12px;
          color: #d4af37;
          padding-bottom: 6px;
          border-bottom: 1px solid rgba(139,105,20,0.4);
        }
        #settings-panel .setting-row {
          display: flex;
          align-items: center;
          margin-bottom: 14px;
          gap: 12px;
        }
        #settings-panel .setting-label {
          flex: 1;
          min-width: 80px;
          color: #c4a77d;
        }
        #settings-panel .setting-control {
          flex: 2;
          display: flex;
          align-items: center;
          gap: 10px;
        }
        #settings-panel .slider {
          flex: 1;
          -webkit-appearance: none;
          appearance: none;
          height: 8px;
          background: rgba(0,0,0,0.3);
          border-radius: 4px;
          outline: none;
          border: 1px solid rgba(139,105,20,0.4);
        }
        #settings-panel .slider::-webkit-slider-thumb {
          -webkit-appearance: none;
          appearance: none;
          width: 18px;
          height: 18px;
          background: linear-gradient(180deg, #ffd35a 0%, #d4af37 100%);
          border-radius: 50%;
          cursor: pointer;
          transition: transform 0.1s;
          border: 2px solid #8b6914;
          box-shadow: 0 2px 4px rgba(0,0,0,0.3);
        }
        #settings-panel .slider::-webkit-slider-thumb:hover {
          transform: scale(1.15);
        }
        #settings-panel .slider::-moz-range-thumb {
          width: 18px;
          height: 18px;
          background: linear-gradient(180deg, #ffd35a 0%, #d4af37 100%);
          border-radius: 50%;
          cursor: pointer;
          border: 2px solid #8b6914;
        }
        #settings-panel .slider-value {
          min-width: 45px;
          text-align: right;
          font-weight: 600;
          color: #ffd700;
        }
        #settings-panel .toggle-btn {
          padding: 8px 20px;
          background: linear-gradient(180deg, #5c4033 0%, #3d2a1c 100%);
          border: 2px solid #6b4423;
          border-radius: 6px;
          color: #c4a77d;
          cursor: pointer;
          font: 13px 'Noto Serif SC', 'SimSun', serif;
          transition: all 0.15s ease;
        }
        #settings-panel .toggle-btn:hover {
          border-color: #8b6914;
          color: #f5e6d3;
        }
        #settings-panel .toggle-btn.active {
          background: linear-gradient(180deg, #8b2020 0%, #5c1515 100%);
          border-color: #c73e3a;
          color: #ffcdd2;
        }
        #settings-panel .footer {
          margin-top: 16px;
          padding-top: 16px;
          border-top: 2px solid rgba(139,105,20,0.4);
          display: flex;
          justify-content: flex-end;
          gap: 12px;
        }
        #settings-panel .footer-btn {
          padding: 10px 24px;
          border-radius: 6px;
          cursor: pointer;
          font: 600 14px 'Noto Serif SC', 'SimSun', serif;
          transition: all 0.15s ease;
        }
        #settings-panel .footer-btn.primary {
          background: linear-gradient(180deg, #ffd35a 0%, #d4af37 100%);
          border: 2px solid #8b6914;
          color: #2a1a08;
          box-shadow: 0 2px 8px rgba(212,175,55,0.3);
        }
        #settings-panel .footer-btn.primary:hover {
          transform: translateY(-2px);
          box-shadow: 0 4px 15px rgba(212,175,55,0.5);
        }
        #settings-panel .footer-btn.secondary {
          background: linear-gradient(180deg, #5c4033 0%, #3d2a1c 100%);
          border: 2px solid #6b4423;
          color: #c4a77d;
        }
        #settings-panel .footer-btn.secondary:hover {
          border-color: #8b6914;
          color: #f5e6d3;
        }
        #settings-panel .community-row {
          display: flex;
          gap: 10px;
          flex-wrap: wrap;
        }
        #settings-panel .community-btn {
          display: inline-flex;
          align-items: center;
          gap: 6px;
          padding: 8px 14px;
          background: linear-gradient(180deg, #5c4033 0%, #3d2a1c 100%);
          border: 1px solid #6b4423;
          border-radius: 6px;
          color: #c4a77d;
          text-decoration: none;
          font: 13px 'Noto Serif SC', 'SimSun', serif;
          cursor: pointer;
          transition: all 0.15s ease;
        }
        #settings-panel .community-btn:hover {
          border-color: #8b6914;
          color: #ffd35a;
          background: linear-gradient(180deg, #6b4a38 0%, #4a3424 100%);
        }
        #settings-panel .community-btn svg {
          width: 18px;
          height: 18px;
          flex-shrink: 0;
        }
        #settings-panel .qr-overlay {
          position: fixed;
          top: 0; left: 0; right: 0; bottom: 0;
          background: rgba(0, 0, 0, 0.7);
          z-index: 400;
          display: flex;
          align-items: center;
          justify-content: center;
        }
        #settings-panel .qr-modal {
          background: linear-gradient(145deg, rgba(74,55,40,0.95) 0%, rgba(45,31,21,0.98) 100%);
          border: 2px solid #8b6914;
          border-radius: 8px;
          padding: 24px;
          text-align: center;
          box-shadow: 0 10px 40px rgba(0,0,0,0.6);
        }
        #settings-panel .qr-modal-title {
          font-size: 16px;
          font-weight: 700;
          color: #ffd35a;
          margin-bottom: 16px;
        }
        #settings-panel .qr-modal img {
          width: 200px;
          height: 200px;
          border-radius: 4px;
          background: #fff;
        }
        #settings-panel .qr-modal-hint {
          margin-top: 12px;
          font-size: 12px;
          color: #9e8b6e;
        }
      </style>
      <div class="settings-panel-wrapper">
        <div class="settings-panel-corner tl"></div>
        <div class="settings-panel-corner tr"></div>
        <div class="settings-panel-corner bl"></div>
        <div class="settings-panel-corner br"></div>
        <div class="settings-panel-title-bar">
          <span class="settings-panel-title">游戏设置</span>
          <button class="close-btn">&times;</button>
        </div>
        <div class="settings-panel-content">
          <div class="settings-section">
            <div class="section-title">音频设置</div>
            <div class="setting-row">
              <span class="setting-label">主音量</span>
              <div class="setting-control">
                <input type="range" class="slider" id="master-volume" min="0" max="100" value="70">
                <span class="slider-value" id="master-volume-value">70%</span>
              </div>
            </div>
            <div class="setting-row">
              <span class="setting-label">音乐音量</span>
              <div class="setting-control">
                <input type="range" class="slider" id="music-volume" min="0" max="100" value="50">
                <span class="slider-value" id="music-volume-value">50%</span>
              </div>
            </div>
            <div class="setting-row">
              <span class="setting-label">音效音量</span>
              <div class="setting-control">
                <input type="range" class="slider" id="sfx-volume" min="0" max="100" value="80">
                <span class="slider-value" id="sfx-volume-value">80%</span>
              </div>
            </div>
            <div class="setting-row">
              <span class="setting-label">静音</span>
              <div class="setting-control">
                <button class="toggle-btn" id="mute-btn">关闭</button>
              </div>
            </div>
          </div>
          <div class="settings-section">
            <div class="section-title">加入社区</div>
            <div class="community-row">
              ${COMMUNITY_LINKS.map((link) =>
                link.qrImage
                  ? `<a class="community-btn" href="javascript:void(0)" data-qr="${link.qrImage}" data-qr-title="${link.label}">${link.icon}<span>${link.label}</span></a>`
                  : `<a class="community-btn" href="${link.url}" target="_blank" rel="noopener noreferrer">${link.icon}<span>${link.label}</span></a>`
              ).join('')}
            </div>
          </div>
          <div class="footer">
            <button class="footer-btn secondary" id="reset-btn">恢复默认</button>
            <button class="footer-btn primary" id="close-settings-btn">确定</button>
          </div>
        </div>
      </div>
    `;

    document.body.appendChild(this.container);
    this.setupEvents();
    this.loadSettings();
  }

  private setupEvents(): void {
    const closeBtn = this.container.querySelector('.close-btn');
    closeBtn?.addEventListener('click', () => this.hide());

    const closeSettingsBtn = this.container.querySelector('#close-settings-btn');
    closeSettingsBtn?.addEventListener('click', () => this.hide());

    const resetBtn = this.container.querySelector('#reset-btn');
    resetBtn?.addEventListener('click', () => this.resetToDefaults());

    // Community QR code links
    this.container.querySelectorAll('.community-btn[data-qr]').forEach((el) => {
      el.addEventListener('click', (e) => {
        e.preventDefault();
        const target = el as HTMLElement;
        const qrImage = target.dataset.qr!;
        const title = target.dataset.qrTitle || '';
        this.showQrModal(title, qrImage);
      });
    });

    // Master volume
    const masterSlider = this.container.querySelector('#master-volume') as HTMLInputElement;
    const masterValue = this.container.querySelector('#master-volume-value');
    masterSlider?.addEventListener('input', () => {
      const value = parseInt(masterSlider.value) / 100;
      audioManager.setMasterVolume(value);
      if (masterValue) masterValue.textContent = `${masterSlider.value}%`;
    });

    // Music volume
    const musicSlider = this.container.querySelector('#music-volume') as HTMLInputElement;
    const musicValue = this.container.querySelector('#music-volume-value');
    musicSlider?.addEventListener('input', () => {
      const value = parseInt(musicSlider.value) / 100;
      audioManager.setMusicVolume(value);
      if (musicValue) musicValue.textContent = `${musicSlider.value}%`;
    });

    // SFX volume
    const sfxSlider = this.container.querySelector('#sfx-volume') as HTMLInputElement;
    const sfxValue = this.container.querySelector('#sfx-volume-value');
    sfxSlider?.addEventListener('input', () => {
      const value = parseInt(sfxSlider.value) / 100;
      audioManager.setSFXVolume(value);
      if (sfxValue) sfxValue.textContent = `${sfxSlider.value}%`;
    });

    // Mute toggle
    const muteBtn = this.container.querySelector('#mute-btn') as HTMLButtonElement;
    muteBtn?.addEventListener('click', () => {
      const settings = audioManager.getSettings();
      const newMuted = !settings.muted;
      audioManager.setMuted(newMuted);
      this.updateMuteButton(newMuted);
    });

    // Escape to close
    window.addEventListener('keydown', (e) => {
      if (e.key === 'Escape' && this.visible) {
        this.hide();
      }
    });
  }

  private loadSettings(): void {
    const settings = audioManager.getSettings();

    const masterSlider = this.container.querySelector('#master-volume') as HTMLInputElement;
    const masterValue = this.container.querySelector('#master-volume-value');
    if (masterSlider) {
      masterSlider.value = String(Math.round(settings.masterVolume * 100));
      if (masterValue) masterValue.textContent = `${masterSlider.value}%`;
    }

    const musicSlider = this.container.querySelector('#music-volume') as HTMLInputElement;
    const musicValue = this.container.querySelector('#music-volume-value');
    if (musicSlider) {
      musicSlider.value = String(Math.round(settings.musicVolume * 100));
      if (musicValue) musicValue.textContent = `${musicSlider.value}%`;
    }

    const sfxSlider = this.container.querySelector('#sfx-volume') as HTMLInputElement;
    const sfxValue = this.container.querySelector('#sfx-volume-value');
    if (sfxSlider) {
      sfxSlider.value = String(Math.round(settings.sfxVolume * 100));
      if (sfxValue) sfxValue.textContent = `${sfxSlider.value}%`;
    }

    this.updateMuteButton(settings.muted);
  }

  private updateMuteButton(muted: boolean): void {
    const muteBtn = this.container.querySelector('#mute-btn') as HTMLButtonElement;
    if (muteBtn) {
      muteBtn.textContent = muted ? '开启' : '关闭';
      muteBtn.classList.toggle('active', muted);
    }
  }

  private showQrModal(title: string, qrImage: string): void {
    const overlay = document.createElement('div');
    overlay.className = 'qr-overlay';
    overlay.innerHTML = `
      <div class="qr-modal">
        <div class="qr-modal-title">扫码加入${title}</div>
        <img src="${qrImage}" alt="${title} QR Code">
        <div class="qr-modal-hint">点击任意处关闭</div>
      </div>
    `;
    overlay.addEventListener('click', () => overlay.remove());
    this.container.appendChild(overlay);
  }

  private resetToDefaults(): void {
    audioManager.setMasterVolume(0.7);
    audioManager.setMusicVolume(0.5);
    audioManager.setSFXVolume(0.8);
    audioManager.setMuted(false);
    this.loadSettings();
  }

  show(): void {
    this.visible = true;
    this.container.classList.add('visible');
    this.loadSettings();
  }

  hide(): void {
    this.visible = false;
    this.container.classList.remove('visible');
  }

  toggle(): void {
    if (this.visible) {
      this.hide();
    } else {
      this.show();
    }
  }

  isVisible(): boolean {
    return this.visible;
  }

  dispose(): void {
    this.container.remove();
  }
}
